import type { loader as rootLoader } from "../root";
import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import type {
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  Link,
  useLoaderData,
  useRouteLoaderData,
} from "@remix-run/react";
import clsx from "clsx";
import { CompanyMembersService } from "~/database/company_members.service";
import { UserService } from "~/database/user.service";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import { generateFileURL } from "~/utils/file/generateFileURL";
import {
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";
import { convertName } from "~/utils/convertName";

export const meta: MetaFunction<
  typeof loader
> = ({ data }) => {
  return [
    {
      title:
        data?.user.name +
        " - メンバー" +
        TITLE_SUFFIX,
    },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
  ];
};

export async function loader({
  request,
  context,
  params,
}: LoaderFunctionArgs) {
  try {
    const userId = params.userId as string;
    const headers = new Headers();
    const supabase = createSbClientSSR(
      headers,
      request,
      context,
    );

    const user = await UserService.getUser(
      supabase,
      userId,
    );

    const members =
      await CompanyMembersService.getPublicMembers(
        supabase,
        user.id,
      );
    console.log("5324235:", 5324235);

    return json(
      {
        user: user,
        members: members,
      },
      {
        headers: headers,
      },
    );
  } catch (e) {
    return redirect("/");
  }
}

export default function UserMembers() {
  const { user, members } =
    useLoaderData<typeof loader>();
  const root =
    useRouteLoaderData<typeof rootLoader>("root");

  return (
    <div
      className={clsx(
        "pb-128 pt-96",
        "max-md:pt-60",
        "max-md:pb-30",
      )}
    >
      <div
        className={clsx(
          "mx-auto w-1300 max-md:w-360",
        )}
      >
        {/* ヘッダー */}
        <div className={clsx("mb-40")}>
          <Link
            to={`/user/${user.id}`}
            className={clsx(
              "text-14 text-gray-500 hover:underline",
            )}
          >
            ← {convertName(user.name)}
            のページに戻る
          </Link>
          <h1
            className={clsx(
              "mt-16 text-24 font-bold",
            )}
          >
            {convertName(user.name)} - メンバー
          </h1>
        </div>

        {/* メンバー一覧 */}
        {members.length === 0 ? (
          <div
            className={clsx(
              "text-center",
              "py-80",
              "text-gray-400",
              "text-16",
            )}
          >
            メンバー情報が登録されていません
          </div>
        ) : (
          <div
            className={clsx(
              "grid grid-cols-4 gap-24",
              "max-md:grid-cols-2",
            )}
          >
            {members.map((member) => (
              <div
                key={member.id}
                className={clsx(
                  "rounded-lg",
                  "text-center",
                  "hover:shadow-lg",
                  "transition-shadow",
                )}
              >
                <div
                  className={clsx(
                    "w-120",
                    "h-120",
                    "rounded-full",
                    "bg-gray-200",
                    "mx-auto",
                    "mb-16",
                    "overflow-hidden",
                  )}
                >
                  {member.profile_image_url ? (
                    <img
                      src={generateFileURL(
                        member.profile_image_url,
                        undefined,
                        root?.basePath,
                      )}
                      alt={member.name}
                      className={clsx(
                        "w-full",
                        "h-full",
                        "object-cover",
                      )}
                    />
                  ) : (
                    <div
                      className={clsx(
                        "w-full",
                        "h-full",
                        "flex",
                        "items-center",
                        "justify-center",
                        "text-gray-400",
                        "text-40",
                      )}
                    >
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z"
                          fill="currentColor"
                        />
                        <path
                          d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <h3
                  className={clsx(
                    "text-16",
                    "font-bold",
                    "mb-4",
                  )}
                >
                  {member.name}
                </h3>
                {member.position && (
                  <p
                    className={clsx(
                      "text-14",
                      "text-gray-600",
                    )}
                  >
                    {member.position}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
